import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Chip,
  Paper,
  Box,
} from '@mui/material';

export interface ViewField {
  key: string;
  label: string;
  value: any;
  type?: 'text' | 'chip' | 'date' | 'currency' | 'email' | 'phone' | 'address' | 'notes' | 'image' | 'custom';
  chipColor?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  chipSx?: any; // Per personalizzare i colori del chip
  size?: { xs: number; sm?: number; md?: number; lg?: number };
  customRender?: (value: any) => React.ReactNode;
}

export interface ViewModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  fields: ViewField[];
  actions?: React.ReactNode;
}

const ViewModal: React.FC<ViewModalProps> = ({
  open,
  onClose,
  title,
  fields,
  actions,
}) => {
  const renderFieldValue = (field: ViewField) => {
    const { value, type = 'text', chipColor = 'default', chipSx, customRender } = field;
    
    // Debug per capire quale campo causa il problema
    if (type === 'chip' && (value === undefined || value === null)) {
      console.warn('Chip with undefined/null value:', field);
    }
    
    // Debug per tutti i campi chip
    if (type === 'chip') {
      console.log('Chip field:', field.key, 'value:', value, 'type:', typeof value);
    }

    if (customRender) {
      try {
        console.log('customRender for field:', field.key, 'value:', value);
        return customRender(value);
      } catch (error) {
        console.error('Error in customRender:', error, 'value:', value, 'field:', field);
        return <Typography variant="body1" color="error">Errore nel rendering</Typography>;
      }
    }

    switch (type) {
      case 'chip':
        const chipLabel = value === null || value === undefined ? 'N/A' : String(value);
        console.log('Rendering chip with label:', chipLabel, 'original value:', value);
        return (
          <Chip
            label={chipLabel}
            color={chipColor}
            size="small"
            sx={chipSx}
          />
        );
      
      case 'date':
        return (
          <Typography variant="body1">
            {value ? new Date(value).toLocaleDateString('it-IT', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }) : '-'}
          </Typography>
        );
      
      case 'currency':
        return (
          <Typography variant="h6" color="primary">
            €{Number(value).toFixed(2)}
          </Typography>
        );
      
      case 'email':
        return (
          <Typography variant="body1" color="primary">
            {value || '-'}
          </Typography>
        );
      
      case 'phone':
        return (
          <Typography variant="body1">
            {value || '-'}
          </Typography>
        );
      
      case 'address':
        return (
          <Typography variant="body1">
            {value || '-'}
          </Typography>
        );
      
      case 'notes':
        return (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              backgroundColor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 1
            }}
          >
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {value || '-'}
            </Typography>
          </Paper>
        );
      
      case 'custom':
        return value || '-';
      
      default:
        return (
          <Typography variant="body1">
            {value || '-'}
          </Typography>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {title}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {fields.map((field) => (
            <Grid 
              key={field.key} 
              size={field.size || { xs: 12, sm: 6 }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {field.label}
              </Typography>
              {renderFieldValue(field)}
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        {actions || (
          <Button onClick={onClose} color="primary">
            Chiudi
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ViewModal;
